import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CalendarDays, User, Clock, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function UpcomingSessions({ onSelectClient }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUpcomingSessions();
  }, []);

  const fetchUpcomingSessions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Filter clients with scheduled sessions and sort by date
      const clientsWithSessions = response.data
        .filter(c => c.next_session_date && c.status === 'active')
        .sort((a, b) => new Date(a.next_session_date) - new Date(b.next_session_date));

      setSessions(clientsWithSessions);
    } catch (error) {
      console.error('Error fetching upcoming sessions:', error);
      toast.error('Failed to load upcoming sessions');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for date grouping
  const isToday = (dateString) => {
    const today = new Date();
    const date = new Date(dateString);
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date.getTime() === today.getTime();
  };

  const isPast = (dateString) => {
    const today = new Date();
    const date = new Date(dateString);
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const isThisWeek = (dateString) => {
    const today = new Date();
    const date = new Date(dateString);
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date <= today) return false; // Not past or today

    const weekFromNow = new Date(today);
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    return date <= weekFromNow;
  };

  const isNextWeek = (dateString) => {
    const today = new Date();
    const date = new Date(dateString);
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    const weekFromNow = new Date(today);
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);

    return date > weekFromNow && date <= twoWeeksFromNow;
  };

  const isLater = (dateString) => {
    const today = new Date();
    const date = new Date(dateString);
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    const twoWeeksFromNow = new Date(today);
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);

    return date > twoWeeksFromNow;
  };

  // Group sessions by time period
  const groupedSessions = {
    overdue: sessions.filter(s => isPast(s.next_session_date)),
    today: sessions.filter(s => isToday(s.next_session_date)),
    thisWeek: sessions.filter(s => isThisWeek(s.next_session_date)),
    nextWeek: sessions.filter(s => isNextWeek(s.next_session_date)),
    later: sessions.filter(s => isLater(s.next_session_date))
  };

  // Format frequency display
  const formatFrequency = (freq, customDays) => {
    if (freq === 'custom' && customDays) return `Every ${customDays} days`;
    const frequencies = {
      weekly: 'Weekly',
      biweekly: 'Biweekly',
      monthly: 'Monthly'
    };
    return frequencies[freq] || 'Custom';
  };

  // Calculate days until session
  const getDaysUntil = (dateString) => {
    const today = new Date();
    const date = new Date(dateString);
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Render session card
  const SessionCard = ({ client }) => {
    const daysUntil = getDaysUntil(client.next_session_date);
    const dateObj = new Date(client.next_session_date);
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    const getStatusColor = () => {
      if (daysUntil < 0) return 'text-red-500';
      if (daysUntil === 0) return 'text-yellow-500';
      if (daysUntil <= 3) return 'text-orange-500';
      return 'text-green-500';
    };

    return (
      <div
        onClick={() => onSelectClient(client)}
        className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-4 hover:border-[var(--accent)] transition-colors cursor-pointer"
        style={{ boxShadow: 'var(--shadow-sm)' }}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <User className="w-4 h-4 text-[var(--accent)]" />
              <h4 className="font-semibold text-[var(--text-primary)]">{client.name}</h4>
            </div>

            <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-1">
              <CalendarDays className="w-4 h-4" />
              <span>{formattedDate}</span>
            </div>

            <div className="flex items-center gap-2 text-sm text-[var(--text-tertiary)]">
              <Clock className="w-4 h-4" />
              <span>{formatFrequency(client.session_frequency, client.custom_interval_days)}</span>
            </div>
          </div>

          <div className="text-right">
            <div className={`text-sm font-semibold ${getStatusColor()}`}>
              {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` :
               daysUntil === 0 ? 'Today' :
               daysUntil === 1 ? 'Tomorrow' :
               `In ${daysUntil}d`}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render group section
  const GroupSection = ({ title, sessions, color }) => {
    if (sessions.length === 0) return null;

    return (
      <div className="mb-6">
        <h3 className={`text-lg font-semibold mb-3 ${color}`}>
          {title} ({sessions.length})
        </h3>
        <div className="space-y-2">
          {sessions.map(client => (
            <SessionCard key={client.id} client={client} />
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[var(--accent)] animate-spin mb-3" />
        <p className="text-[var(--text-secondary)]">Loading upcoming sessions...</p>
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <CalendarDays className="w-16 h-16 text-[var(--text-tertiary)] mb-4" />
        <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
          No Scheduled Sessions
        </h3>
        <p className="text-[var(--text-secondary)] text-center max-w-md">
          You don't have any upcoming sessions scheduled. Set session dates for your clients to see them here.
        </p>
      </div>
    );
  }

  return (
    <div className="pb-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-2">
          Upcoming Sessions
        </h2>
        <p className="text-[var(--text-secondary)]">
          View and manage all scheduled client sessions
        </p>
      </div>

      <GroupSection
        title="Overdue"
        sessions={groupedSessions.overdue}
        color="text-red-500"
      />

      <GroupSection
        title="Today"
        sessions={groupedSessions.today}
        color="text-yellow-500"
      />

      <GroupSection
        title="This Week"
        sessions={groupedSessions.thisWeek}
        color="text-[var(--accent)]"
      />

      <GroupSection
        title="Next Week"
        sessions={groupedSessions.nextWeek}
        color="text-[var(--text-primary)]"
      />

      <GroupSection
        title="Later"
        sessions={groupedSessions.later}
        color="text-[var(--text-secondary)]"
      />
    </div>
  );
}

export default UpcomingSessions;
